import { api } from "@slapinc/slappos-backend/api";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useCallback, useMemo, useState } from "react";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { CreateRoleModal } from "@/components/modals/create-role-modal";
import { EditRolePermissionsModal } from "@/components/modals/edit-role-permissions-modal";
import { PermissionGuard } from "@/components/permission-guard";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RolesTable } from "@/features/roles/_components/roles-table";
import { useMe } from "@/hooks/use-me";
import { handleConvexError } from "@/lib/errors";
import type { CreateRoleInput } from "@/lib/schemas/role";
import { toast } from "@/lib/toast";
import type { Role } from "@/lib/types/member";

export const Route = createFileRoute("/(dashboard)/settings/roles")({
	component: () => (
		<PermissionGuard scope="roles.read">
			<RolesPage />
		</PermissionGuard>
	),
});

function RolesPage() {
	// Get current user to find active organization
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	// State

	// Modal state
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [selectedRole, setSelectedRole] = useState<Role | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleteErrorDialogOpen, setDeleteErrorDialogOpen] = useState(false);
	const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

	// Mutations
	const createRole = useMutation(api.features.roles.web.create);
	const updatePermissions = useMutation(
		api.features.roles.web.updatePermissions,
	);
	const deleteRole = useMutation(api.features.roles.web.remove);

	// Mutation loading states
	const [isCreating, setIsCreating] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Query roles
	const rolesQuery = useQuery(
		api.features.roles.web.list,
		orgId ? { orgId } : "skip",
	);

	// Query members to compute counts per role
	const membersQuery = useQuery(
		api.features.members.web.list,
		orgId ? { orgId } : "skip",
	);

	const roles = (rolesQuery ?? []) as Role[];
	const isLoading = rolesQuery === undefined;

	// Compute member counts per role
	const memberCounts = useMemo(() => {
		const counts = new Map<Id<"organizationRoles">, number>();
		for (const member of membersQuery ?? []) {
			const roleId = member.roleId;
			counts.set(roleId, (counts.get(roleId) ?? 0) + 1);
		}
		return counts;
	}, [membersQuery]);

	// Handlers
	const handleEdit = useCallback((role: Role) => {
		setSelectedRole(role);
		setEditModalOpen(true);
	}, []);

	const handleDelete = useCallback(
		(role: Role) => {
			const count = memberCounts.get(role._id) ?? 0;
			setRoleToDelete(role);

			if (count > 0) {
				// Show error dialog if role has members
				setDeleteErrorDialogOpen(true);
			} else {
				// Show confirmation dialog
				setDeleteDialogOpen(true);
			}
		},
		[memberCounts],
	);

	const confirmDelete = useCallback(async () => {
		if (!roleToDelete || !orgId) return;

		setIsDeleting(true);
		try {
			await deleteRole({ orgId, roleId: roleToDelete._id });
			toast.success(<>Role deleted</>);
			setDeleteDialogOpen(false);
			setRoleToDelete(null);
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Failed to delete role";
			if (errorMessage.includes("Cannot delete role with assigned members")) {
				setDeleteDialogOpen(false);
				setDeleteErrorDialogOpen(true);
			} else {
				handleConvexError(error, "Failed to delete role");
			}
		} finally {
			setIsDeleting(false);
		}
	}, [roleToDelete, orgId, deleteRole]);

	const handleCreateRole = useCallback(
		async (data: CreateRoleInput) => {
			if (!orgId) return;

			setIsCreating(true);
			try {
				await createRole({
					orgId,
					name: data.name,
					preset: data.preset,
					permissions: data.permissions,
				});
				toast.success(<>Role created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create role");
				throw error;
			} finally {
				setIsCreating(false);
			}
		},
		[orgId, createRole],
	);

	const handleUpdatePermissions = useCallback(
		async (permissions: string[]) => {
			if (!orgId || !selectedRole) return;

			setIsUpdating(true);
			try {
				await updatePermissions({
					orgId,
					roleId: selectedRole._id,
					permissions,
				});
				toast.success(<>Permissions updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update permissions");
				throw error;
			} finally {
				setIsUpdating(false);
			}
		},
		[orgId, selectedRole, updatePermissions],
	);

	return (
		<PageContent
			title="Roles"
			controls={
				<Can scope="roles.create">
					<ActionHeaderButton
						label="Add Role"
						onClick={() => setCreateModalOpen(true)}
					/>
				</Can>
			}
		>
			<RolesTable
				data={roles}
				memberCounts={memberCounts}
				loading={isLoading}
				onEdit={handleEdit}
				onDelete={handleDelete}
			/>

			{/* Create Modal */}
			<CreateRoleModal
				open={createModalOpen}
				onOpenChange={setCreateModalOpen}
				onSubmit={handleCreateRole}
				isSubmitting={isCreating}
			/>

			{/* Edit Permissions Modal */}
			<EditRolePermissionsModal
				open={editModalOpen}
				onOpenChange={setEditModalOpen}
				role={selectedRole}
				onSubmit={handleUpdatePermissions}
				isSubmitting={isUpdating}
			/>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete role?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete "{roleToDelete?.name}"? This
							action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDelete}
							disabled={isDeleting}
						>
							{isDeleting ? "Deleting..." : "Delete"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Delete Error Dialog (role has members) */}
			<AlertDialog
				open={deleteErrorDialogOpen}
				onOpenChange={setDeleteErrorDialogOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Cannot delete role</AlertDialogTitle>
						<AlertDialogDescription>
							The role "{roleToDelete?.name}" has{" "}
							{memberCounts.get(roleToDelete?._id as Id<"organizationRoles">) ??
								0}{" "}
							member(s) assigned to it. Please reassign or remove these members
							before deleting this role.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>OK</AlertDialogCancel>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
